import { useState, type ReactElement } from 'react';

import { ConfirmModal } from '@/components/ui/ConfirmModal';
import { useToast } from '@/components/ui/toast';
import { useStepUpLogin } from '@/hooks/useStepUpLogin';
import { isStepUpRequired } from '@/lib/api';
import { dayOfDateOnly } from '@/lib/time';

import { stageActErrorCopy } from '../act-errors';
import { returnWarning, stageName } from '../action-presentation';
import { modalFor, type EditorModal } from '../editor-modal';
import { useFlowActions } from '../use-flow-actions';
import { approvalToast } from '../approval-feedback';
import { suggestedApprovalDate } from '../training-stage';
import { useTrainingStage } from '../use-training-stage';
import { type FlowAction, type FlowCapabilities } from '../types';
import { ImpactPanorama } from '@/features/document-relations/components/ImpactPanorama';
import { ApproveModal } from './ApproveModal';
import { EditorHeader } from './EditorHeader';
import { EditorStack } from './EditorStack';
import { OnlineEditorPanel } from './OnlineEditorPanel';
import { ReturnModal } from './ReturnModal';
import { StageActModals } from './StageActModals';

interface Props {
  caps: FlowCapabilities;
  companyId: string | null;
}

export function EditorBody({ caps, companyId }: Props): ReactElement {
  // G4: vigencia sugerida al firmar (hoy + plazo de capacitación), misma consulta del stepper.
  const training = useTrainingStage(caps.entryId, { companyId, enabled: true });
  const actions = useFlowActions();
  const { showToast } = useToast();
  const [modal, setModal] = useState<EditorModal>(null);
  // La firma pidió re-autenticación (step-up): el modal ofrece confirmar identidad.
  const [stepUp, setStepUp] = useState(false);
  const stepUpLogin = useStepUpLogin();

  const canEdit = caps.availableActions.find((a) => a.action === 'edit-online')?.enabled ?? false;
  const stageActions = caps.availableActions.filter((a) => a.action !== 'edit-online');
  const pending =
    actions.transition.isPending ||
    actions.returnDocument.isPending ||
    actions.approve.isPending ||
    actions.decommission.isPending ||
    actions.startModification.isPending ||
    actions.conformity.isPending ||
    actions.closeApproval.isPending;

  // Toda acción con consecuencia (avanzar de etapa, iniciar modificación, devolver,
  // firmar, dar de baja) pasa por confirmación; ninguna se ejecuta al primer click.
  const onSelect = (a: FlowAction): void => setModal(modalFor(a));

  const confirmTransition = (): void => {
    if (modal?.kind !== 'transition' || caps.flowInstanceId === null) {
      return;
    }
    const flowInstanceId = caps.flowInstanceId;
    const toStatus = modal.toStatus;
    actions.transition.mutate(
      { flowInstanceId, toStatus },
      {
        onSuccess: () => {
          setModal(null);
          showToast(`Documento enviado a ${stageName(toStatus)}`);
        },
        onError: (err) =>
          showToast(stageActErrorCopy(err, 'No se pudo avanzar el documento'), 'error'),
      },
    );
  };

  const confirmStartModification = (): void => {
    actions.startModification.mutate(
      { entryId: caps.entryId },
      {
        onSuccess: () => {
          setModal(null);
          showToast('Modificación iniciada: nuevo borrador');
        },
        onError: () => showToast('No se pudo iniciar la modificación', 'error'),
      },
    );
  };

  const confirmReturn = (reason: string): void => {
    if (modal?.kind !== 'return' || caps.flowInstanceId === null) {
      return;
    }
    const toStatus = modal.toStatus;
    actions.returnDocument.mutate(
      { flowInstanceId: caps.flowInstanceId, toStatus, reason },
      {
        onSuccess: () => {
          setModal(null);
          showToast(`Documento devuelto a ${stageName(toStatus)}`);
        },
        onError: (err) =>
          showToast(stageActErrorCopy(err, 'No se pudo devolver el documento'), 'error'),
      },
    );
  };

  const confirmApprove = (effectiveDate: string): void => {
    if (caps.targetVersion === null) {
      return;
    }
    actions.approve.mutate(
      { entryId: caps.entryId, targetVersion: caps.targetVersion, effectiveDate },
      {
        onSuccess: (after) => {
          setModal(null);
          // G2-D: firma parcial o aprobación sellada, según el servidor tras la firma.
          showToast(approvalToast(after));
        },
        onError: (err) =>
          isStepUpRequired(err)
            ? setStepUp(true)
            : showToast(stageActErrorCopy(err, 'No se pudo firmar el documento'), 'error'),
      },
    );
  };

  const confirmDecommission = (): void => {
    actions.decommission.mutate(
      { entryId: caps.entryId },
      {
        onSuccess: () => {
          setModal(null);
          showToast('Documento dado de baja');
        },
        onError: () => showToast('No se pudo dar de baja el documento', 'error'),
      },
    );
  };

  // Confirmaciones no destructivas (transición / iniciar modificación) y destructiva
  // (baja) resueltas por un solo ConfirmModal según el estado del modal.
  const confirm =
    modal?.kind === 'decommission'
      ? {
          title: 'Dar de baja el documento',
          message:
            'El documento pasará a obsoleto y su código quedará inutilizado. Esta acción no se revierte.',
          confirmLabel: 'Dar de baja',
          destructive: true,
          busy: actions.decommission.isPending,
          onConfirm: confirmDecommission,
        }
      : modal?.kind === 'transition'
        ? {
            title: 'Enviar documento',
            message: `El documento pasará a la etapa de ${stageName(modal.toStatus)} y quedará en manos del siguiente responsable.`,
            confirmLabel: 'Enviar',
            destructive: false,
            busy: actions.transition.isPending,
            onConfirm: confirmTransition,
          }
        : modal?.kind === 'start-modification'
          ? {
              title: 'Iniciar modificación',
              message: (
                <>
                  Se abrirá un nuevo borrador sobre el documento vigente para editarlo. La versión
                  vigente sigue activa hasta aprobar la nueva.
                  <ImpactPanorama entryId={caps.entryId} />
                </>
              ),
              confirmLabel: 'Iniciar',
              destructive: false,
              busy: actions.startModification.isPending,
              onConfirm: confirmStartModification,
            }
          : null;

  return (
    <>
      <EditorHeader caps={caps} />

      <div className="g-84 rise d1">
        <OnlineEditorPanel
          entryId={caps.entryId}
          code={caps.code}
          companyId={companyId}
          canEdit={canEdit}
        />
        <EditorStack
          caps={caps}
          companyId={companyId}
          stageActions={stageActions}
          onSelect={onSelect}
          pending={pending}
        />
      </div>

      <ReturnModal
        open={modal?.kind === 'return'}
        toLabel={modal?.kind === 'return' ? stageName(modal.toStatus) : ''}
        busy={actions.returnDocument.isPending}
        warning={returnWarning(caps)}
        onCancel={() => setModal(null)}
        onConfirm={confirmReturn}
      />
      <ApproveModal
        open={modal?.kind === 'approve'}
        busy={actions.approve.isPending}
        onCancel={() => {
          setModal(null);
          setStepUp(false);
        }}
        onConfirm={confirmApprove}
        fixedDate={dayOfDateOnly(caps.roundEffectiveDate)}
        suggestedDate={suggestedApprovalDate(training.data)}
        stepUpRequired={stepUp}
        onReauthenticate={stepUpLogin}
      />
      <StageActModals modal={modal} caps={caps} actions={actions} onClose={() => setModal(null)} />
      <ConfirmModal
        open={confirm !== null}
        title={confirm?.title ?? ''}
        message={confirm?.message ?? ''}
        confirmLabel={confirm?.confirmLabel}
        destructive={confirm?.destructive}
        busy={confirm?.busy}
        onConfirm={() => confirm?.onConfirm()}
        onCancel={() => setModal(null)}
      />
    </>
  );
}
